


import { createSlice } from '@reduxjs/toolkit';

// Per-chart Y-axis overrides, keyed by chartName: { [chartName]: { low?, high? } }.
// Keyed by chart (not timeframe) on purpose — the same fixed scale then applies
// across every timeframe, since a timeframe only changes which table is read.
const loadAxisRanges = () => {
    try { return JSON.parse(localStorage.getItem('data_axisRanges')) || {}; }
    catch (e) { return {}; }
};

const initialState = {
    table0_secs:   {},
    table1_mins:   {},
    table2_hours:  {},
    table3_days:   {},
    table4_weeks:  {},
    table5_months: {},
    table6_years:  {},
    chartNames:        [],
    // chartName -> 'map'. numb/bool charts are NOT registered here; absence
    // means "regular graph" (line vs bar stays inferred from the data, as
    // before). Map charts can't be inferred, so they carry their type.
    chartTypes:        {},
    // Last known location per person, per map chart:
    //   { chartName: { person: { lat, lng, fixMs, tid? } } }
    // Latest-only (no history), mirrored from the back-end's in-memory store.
    locations:         {},
    selectedChart:     localStorage.getItem('data_selectedChart') || null,
    selectedTimeframe: localStorage.getItem('data_selectedTimeframe') || 'mins',
    seriesKeyMap:      {},
    axisRanges:        loadAxisRanges(),
};


const RAW_RETAIN_MS   = 2.5 * 60 * 60 * 1000;
const RAW_MAX_POINTS  = 40000;           // memory backstop only; trips on a runaway feed
const CONDENSED_CAP   = 3500;            // tables 1–6: weeks needs 3,024 to fill its view

const toMs = (time) => time instanceof Date ? time.getTime() : (typeof time === 'number' ? time : Date.parse(time));

const dataSlice = createSlice({
    name: 'data',
    initialState,
    reducers: {

        setData: (state, action) => {
            const flatData = action.payload;
            for (const tableKey in flatData) {
                if (state[tableKey] === undefined) continue;
                state[tableKey] = flatData[tableKey];
                // collect chartNames
                for (const chartName in flatData[tableKey]) {
                    if (!state.chartNames.includes(chartName)) {
                        state.chartNames.push(chartName);
                    }
                }
            }
        },

		updateData: (state, action) => {
			const { target, chartName, seriesKey, time, data } = action.payload;
			if (state[target] === undefined) return;
			if (!state[target][chartName]) state[target][chartName] = {};
			if (!state[target][chartName][seriesKey]) state[target][chartName][seriesKey] = [];

			const series = state[target][chartName][seriesKey];
			series.push({ time, data });

			if (target === 'table0_secs') {
				// raw variable-rate data → prune by TIME; count is just a runaway guard
				const newest = toMs(time);
				while (series.length > 2 && newest - toMs(series[0].time) > RAW_RETAIN_MS) series.shift();
				if (series.length > RAW_MAX_POINTS) series.shift();
			} else {
				// condensed tables produce at a fixed cadence → count maps cleanly to time
				if (series.length > CONDENSED_CAP) series.shift();
			}

			if (!state.chartNames.includes(chartName)) state.chartNames.push(chartName);
		},

        // Full snapshot from /get/locations (login / reconnect refetch).
        // Replaces the locations block wholesale and registers each map
        // chart in the menu.
        setLocations: (state, action) => {
            const snapshot = action.payload || {};
            state.locations = snapshot;
            for (const chartName in snapshot) {
                state.chartTypes[chartName] = 'map';
                if (!state.chartNames.includes(chartName)) state.chartNames.push(chartName);
            }
        },

        // Live delta from the 'map' socket event: { chartName: { person: point } }.
        // The back-end already dropped stale fixes, so this merges blindly.
        updateLocations: (state, action) => {
            const values = action.payload || {};
            for (const chartName in values) {
                if (!state.locations[chartName]) state.locations[chartName] = {};
                Object.assign(state.locations[chartName], values[chartName]);
                state.chartTypes[chartName] = 'map';
                if (!state.chartNames.includes(chartName)) state.chartNames.push(chartName);
            }
        },

        setSelectedChart: (state, action) => {
            state.selectedChart = action.payload;
            try { localStorage.setItem('data_selectedChart', action.payload); } catch(e) {}
        },

        setSelectedTimeframe: (state, action) => {
            state.selectedTimeframe = action.payload;
            try { localStorage.setItem('data_selectedTimeframe', action.payload); } catch(e) {}
        },

        updateSeriesKeyMap: (state, action) => {
            Object.assign(state.seriesKeyMap, action.payload);
        },

        // Set/clear a chart's manual Y-axis bounds. Each bound is independent:
        // pass a finite number to pin it, or null/undefined to leave it on auto.
        // When neither bound is finite the entry is removed (full auto).
        setAxisRange: (state, action) => {
            const { chartName, low, high } = action.payload;
            if (!chartName) return;
            const entry = {};
            if (Number.isFinite(low))  entry.low  = low;
            if (Number.isFinite(high)) entry.high = high;
            if (Object.keys(entry).length === 0) {
                delete state.axisRanges[chartName];
            } else {
                state.axisRanges[chartName] = entry;
            }
            try { localStorage.setItem('data_axisRanges', JSON.stringify(state.axisRanges)); } catch (e) {}
        }
    },
});

export const { setData, updateData, setLocations, updateLocations, setSelectedChart, setSelectedTimeframe, updateSeriesKeyMap, setAxisRange } = dataSlice.actions;
export default dataSlice.reducer;